/**
 * HTTP transport for module actions uses native `fetch` + `AbortController`
 * (see plan: no extra client library without explicit approval).
 */

import type {
  ModuleActionPayload,
  ModuleID,
  ModuleInstanceKey,
  VersionString,
  ViewID,
} from "@bestowinc/tinkertoy-core";

import { withInstrumentedAction } from "./action-observability";
import {
  moduleActionScope,
  releaseActionAbortSlot,
  supersedeActionRequest,
} from "./action-abort-registry";
import { actionUrl, currentRoute } from "./action-request";
import { TinkertoyActionError } from "./action-errors";
import { parseActionResponseBody } from "./parse-action-response";

export type SubmitModuleActionArgs = {
  apiBaseUrl: string;
  moduleId: ModuleID;
  /**
   * The exact module version the client rendered. Required so the server can
   * cross-check the request against the view manifest's pinned version and
   * reject stale or forged requests.
   */
  moduleVersion: VersionString;
  /**
   * Which of the view's module refs rendered the module, so the server
   * resolves that ref's data params. Absent for slot-rendered modules.
   */
  instanceKey?: ModuleInstanceKey;
  /**
   * Identity of the view that produced the loader response which rendered the
   * module. Required so the server can confirm the module is part of that
   * view's manifest before invoking the action.
   */
  viewId: ViewID;
  viewVersion: VersionString;
  actionName: string;
  data: unknown;
};

/**
 * POST serialized action payload to the Tinkertoy server `action` endpoint and
 * return typed `data` on success.
 *
 * The payload always carries `scope: "module"` plus the view + module identity
 * fields the server uses to validate the request against the manifest pinned
 * for that view (see {@link ModuleActionPayload}). This is in addition to the
 * `moduleId` / `actionName` pair, which on their own would not be enough for
 * the server to safely resolve a handler.
 *
 * Every non-abort failure path throws a {@link TinkertoyActionError}:
 * - a JSON body that parses as one of the error variants of
 *   `ActionResponseSchema` becomes a `TinkertoyActionError` with the
 *   appropriate `code`;
 * - a non-JSON or malformed body becomes a generic action error;
 * - a non-OK response with no parseable error body falls back to an HTTP
 *   error with `code: "HTTP_${status}"`.
 *
 * @throws {TinkertoyActionError} validation / handler / pipeline / transport errors
 * @throws {DOMException} `AbortError` when superseded or cancelled
 */
export async function submitModuleAction({
  apiBaseUrl,
  moduleId,
  moduleVersion,
  instanceKey,
  viewId,
  viewVersion,
  actionName,
  data,
}: SubmitModuleActionArgs): Promise<unknown> {
  return withInstrumentedAction(
    {
      actionScope: "module",
      actionName,
      viewId,
      viewVersion,
      moduleId,
      moduleVersion,
    },
    () =>
      submitModuleActionInner({
        apiBaseUrl,
        moduleId,
        moduleVersion,
        instanceKey,
        viewId,
        viewVersion,
        actionName,
        data,
      }),
  );
}

async function submitModuleActionInner({
  apiBaseUrl,
  moduleId,
  moduleVersion,
  instanceKey,
  viewId,
  viewVersion,
  actionName,
  data,
}: SubmitModuleActionArgs): Promise<unknown> {
  const scope = moduleActionScope(moduleId, instanceKey);
  const signal = supersedeActionRequest(scope, actionName);

  try {
    const payload: ModuleActionPayload = {
      scope: "module",
      moduleId,
      moduleVersion,
      instanceKey,
      viewId,
      viewVersion,
      actionName,
      data,
      route: currentRoute(),
    };

    const response = await fetch(actionUrl(apiBaseUrl), {
      method: "POST",
      signal,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }).catch((err: unknown) => {
      if (err instanceof DOMException && err.name === "AbortError") {
        throw err;
      }
      throw new TinkertoyActionError(
        err instanceof Error ? err.message : "Network request failed.",
        { code: "network_error" },
      );
    });

    let body: unknown;
    const contentType = response.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) {
      try {
        body = await response.json();
      } catch {
        throw new TinkertoyActionError("Server returned invalid JSON.");
      }
    } else if (response.ok) {
      throw new TinkertoyActionError("Server returned a non-JSON response.");
    }

    if (body !== undefined) {
      // The server's contract is that every response — success or failure —
      // is a JSON body matching ActionResponseSchema. parseActionResponseBody
      // throws on every non-success variant; returning its result covers both
      // 2xx-with-body and non-2xx-with-error-body.
      return await parseActionResponseBody(body);
    }

    if (!response.ok) {
      throw new TinkertoyActionError(
        response.statusText || `Action request failed (${response.status})`,
        { code: `HTTP_${response.status}` },
      );
    }

    throw new TinkertoyActionError("Empty action response.");
  } finally {
    releaseActionAbortSlot(scope, actionName, signal);
  }
}
